// client/src/pages/admin/project-steps/Step4FloorPlan.tsx
import React, { useState, useRef } from 'react';
import { ProjectForm, FloorPlan, SubUnit } from './types';
import { generateFloorPlanSampleExcel, parseFloorPlanExcel } from '../../../utils/excelTemplate';
import { toast } from 'react-toastify';

interface Props {
    activeStep4Tab: '2d' | '3d' | null;
    setActiveStep4Tab: (tab: '2d' | '3d' | null) => void;
    floorPlans: FloorPlan[];
    setFloorPlans: React.Dispatch<React.SetStateAction<FloorPlan[]>>;
    getImageUrl?: (path: string) => string;
    QuickUpdateButton?: React.FC;
    errors?: Record<string, boolean>;
    form: ProjectForm;
    setForm?: React.Dispatch<React.SetStateAction<ProjectForm>>;
    scoreSettings?: any;
}

const Step4FloorPlan: React.FC<Props> = ({ 
    floorPlans, 
    setFloorPlans, 
    errors
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [activeConfigIndex, setActiveConfigIndex] = useState<number>(0);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [newConfigName, setNewConfigName] = useState('');
    const [editingConfigIndex, setEditingConfigIndex] = useState<number | null>(null);
    
    const [isVariantModalOpen, setIsVariantModalOpen] = useState(false);
    const [editingVariantIndex, setEditingVariantIndex] = useState<number | null>(null);
    const [currentVariant, setCurrentVariant] = useState<SubUnit>({
        subUnitType: '',
        subUnitVariant: '',
        price: '',
        facing: [],
        reraArea: '',
        carpetArea: '',
        builtUpArea: '',
        superBuiltUpArea: '',
        details: [{ label: '', value: '' }],
        image: '',
        imagePreview: '',
        image2D: '',
        image2DPreview: '',
        image3D: '',
        image3DPreview: '',
        infoImage: '',
        infoImagePreview: '',
        virtual3DLink: '',
        numberOfUnits: ''
    });

    const activePlan = floorPlans[activeConfigIndex];

    const handleImportExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const mappedData = await parseFloorPlanExcel(file);
            if (mappedData.length === 0) {
                toast.error("Excel file is empty or invalid format.");
                return;
            }

            const newFloorPlans: FloorPlan[] = [...floorPlans.filter(fp => fp.unitType)];
            
            mappedData.forEach(row => {
                if (!row.unitType || !row.subUnitVariant) return;

                let fp = newFloorPlans.find(item => item.unitType.toLowerCase() === row.unitType.toLowerCase());
                if (!fp) {
                    fp = {
                        unitType: row.unitType,
                        subUnits: []
                    };
                    newFloorPlans.push(fp);
                }

                let details = [{ label: '', value: '' }];
                if (typeof row.details === 'string' && row.details.trim()) {
                    details = row.details.split(',').map((item: string) => {
                        const [label, ...valParts] = item.split(':');
                        return {
                            label: label ? label.trim() : '',
                            value: valParts.length > 0 ? valParts.join(':').trim() : ''
                        };
                    }).filter((d: any) => d.label);
                }

                if (details.length === 0) {
                    details = [{ label: '', value: '' }];
                }

                fp.subUnits.push({
                    subUnitType: row.subUnitType || 'Residential',
                    subUnitVariant: row.subUnitVariant,
                    price: String(row.price || ''),
                    facing: typeof row.facing === 'string' ? row.facing.split(',').map((f: string) => f.trim()) : [],
                    reraArea: String(row.reraArea || ''),
                    carpetArea: String(row.carpetArea || ''),
                    builtUpArea: String(row.builtUpArea || ''),
                    superBuiltUpArea: String(row.superBuiltUpArea || ''),
                    details: details,
                    image: '',
                    imagePreview: '',
                    image2D: '',
                    image2DPreview: '',
                    image3D: '',
                    image3DPreview: '',
                    infoImage: '',
                    infoImagePreview: '',
                    virtual3DLink: row.virtual3DLink || '',
                    numberOfUnits: row.numberOfUnits || ''
                });
            });

            if (newFloorPlans.length > 1 && newFloorPlans[0].unitType === '') {
                newFloorPlans.shift();
            }

            setFloorPlans(newFloorPlans);
            toast.success("Excel floor plans imported successfully!");
        } catch (err) {
            console.error("Floor plan Excel parse error:", err);
            toast.error("Failed to parse Excel file.");
        } finally {
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSaveConfig = () => {
        if (!newConfigName.trim()) return;
        const newPlans = [...floorPlans];
        if (editingConfigIndex !== null) {
            newPlans[editingConfigIndex].unitType = newConfigName.trim();
            toast.success("Configuration type updated!");
        } else {
            newPlans.push({ unitType: newConfigName.trim(), subUnits: [] });
            setActiveConfigIndex(floorPlans.length);
            toast.success("Configuration type added!");
        }
        setFloorPlans(newPlans);
        setIsConfigModalOpen(false);
        setNewConfigName('');
        setEditingConfigIndex(null);
    };

    const handleOpenVariantModal = (variantIndex: number | null) => {
        if (!activePlan) return;
        
        if (variantIndex !== null) {
            setEditingVariantIndex(variantIndex);
            setCurrentVariant({ ...activePlan.subUnits[variantIndex] });
        } else {
            setEditingVariantIndex(null);
            setCurrentVariant({
                subUnitType: '',
                subUnitVariant: '',
                price: '',
                facing: [],
                reraArea: '',
                carpetArea: '',
                builtUpArea: '',
                superBuiltUpArea: '',
                details: [{ label: '', value: '' }],
                image: '',
                imagePreview: '',
                image2D: '',
                image2DPreview: '',
                image3D: '',
                image3DPreview: '',
                infoImage: '',
                infoImagePreview: '',
                virtual3DLink: '',
                numberOfUnits: ''
            });
        }
        setIsVariantModalOpen(true);
    };

    const handleSaveVariant = () => {
        if (!activePlan) return;

        const newPlans = [...floorPlans];
        if (editingVariantIndex !== null) {
            newPlans[activeConfigIndex].subUnits[editingVariantIndex] = currentVariant;
        } else {
            newPlans[activeConfigIndex].subUnits.push(currentVariant);
        }
        setFloorPlans(newPlans);
        setIsVariantModalOpen(false);
    };

    const handleDeleteVariant = (variantIndex: number) => {
        if (!activePlan) return;
        if (!window.confirm("Are you sure you want to delete this variant?")) return;

        const newPlans = [...floorPlans];
        newPlans[activeConfigIndex].subUnits = newPlans[activeConfigIndex].subUnits.filter((_, idx) => idx !== variantIndex);
        setFloorPlans(newPlans);
    };

    // Calculate aggregated info
    let totalUnits = 0;
    let minCarpet = Infinity, maxCarpet = -Infinity;
    let minSuper = Infinity, maxSuper = -Infinity;
    let minPrice = Infinity, maxPrice = -Infinity;
    
    if (activePlan) {
        activePlan.subUnits.forEach(su => {
            const units = parseInt(su.numberOfUnits || '0');
            if (!isNaN(units)) totalUnits += units;
            
            const carpet = parseFloat(su.carpetArea || '0');
            if (!isNaN(carpet) && carpet > 0) {
                if (carpet < minCarpet) minCarpet = carpet;
                if (carpet > maxCarpet) maxCarpet = carpet;
            }
            
            const superArea = parseFloat(su.superBuiltUpArea || '0');
            if (!isNaN(superArea) && superArea > 0) {
                if (superArea < minSuper) minSuper = superArea;
                if (superArea > maxSuper) maxSuper = superArea;
            }

            // Price might be a string like "45,50,000" or "45.5 L". Just try basic parsing if purely numeric
            const priceClean = (su.price || '').replace(/[^0-9.]/g, '');
            const priceVal = parseFloat(priceClean);
            if (!isNaN(priceVal) && priceVal > 0) {
                if (priceVal < minPrice) minPrice = priceVal;
                if (priceVal > maxPrice) maxPrice = priceVal;
            }
        });
    }

    const numberToWords = (numStr: string) => {
        // very simplified dummy logic for now, in reality you'd parse correctly
        if (!numStr) return '';
        return numStr; // Fallback to just the string for now, user can enter text like "(Forty Five Lakh)" in price if needed, or we just display what they enter.
    };

    return (
        <div className="space-y-6">
            {/* Header matches screenshot */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-sm">6</div>
                    <div>
                        <h3 className="text-xl font-bold text-slate-900">Floor Variants (Configuration & Units)</h3>
                        <p className="text-sm text-slate-500">Manage floor configurations and unit-wise details with pricing & area.</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button type="button" className="px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-slate-50 transition-colors">
                        <i className="fa-regular fa-circle-question"></i> How it works?
                    </button>
                    <button type="button" className="px-4 py-2 bg-white border border-slate-200 text-blue-600 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-blue-50 transition-colors">
                        <i className="fa-solid fa-bolt"></i> Quick Add
                    </button>
                    <div className="ml-4 pl-4 border-l border-slate-200">
                        <span className="text-sm font-bold text-slate-800">Configuration: <span className="text-emerald-600">{activePlan?.unitType || 'None'}</span></span>
                    </div>
                </div>
            </div>

            {/* Configuration Tabs */}
            <div className="flex items-center gap-4 overflow-x-auto pb-4 mb-4 border-b border-slate-200">
                {floorPlans.map((plan, idx) => {
                    const isActive = activeConfigIndex === idx;
                    const totalConfigUnits = plan.subUnits.reduce((acc, curr) => acc + (parseInt(curr.numberOfUnits || '0') || 0), 0);
                    
                    const tabThemes = [
                        { activeBg: 'bg-emerald-50', activeBorder: 'border-emerald-500', activeText: 'text-emerald-800', inactiveBorder: 'border-slate-200', inactiveText: 'text-slate-600', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600' }, // 1 BHK
                        { activeBg: 'bg-orange-50', activeBorder: 'border-orange-400', activeText: 'text-orange-800', inactiveBorder: 'border-slate-200', inactiveText: 'text-slate-600', iconBg: 'bg-orange-100', iconText: 'text-orange-500' }, // 2 BHK
                        { activeBg: 'bg-purple-50', activeBorder: 'border-purple-400', activeText: 'text-purple-800', inactiveBorder: 'border-slate-200', inactiveText: 'text-slate-600', iconBg: 'bg-purple-100', iconText: 'text-purple-500' }, // 3 BHK
                        { activeBg: 'bg-blue-50', activeBorder: 'border-blue-400', activeText: 'text-blue-800', inactiveBorder: 'border-slate-200', inactiveText: 'text-slate-600', iconBg: 'bg-blue-100', iconText: 'text-blue-500' }, // 4 BHK
                    ];
                    const theme = tabThemes[idx % tabThemes.length];

                    return (
                        <div key={idx} className="relative group shrink-0">
                            <button 
                                type="button"
                                onClick={() => setActiveConfigIndex(idx)}
                                className={`flex items-center gap-3 px-5 py-3 pr-16 rounded-xl border min-w-[180px] transition-all ${isActive ? `${theme.activeBorder} ${theme.activeBg} ${theme.activeText} shadow-sm` : `${theme.inactiveBorder} bg-white ${theme.inactiveText} hover:border-slate-300`}`}
                            >
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl ${theme.iconBg} ${theme.iconText}`}>
                                    <i className="fa-solid fa-building-user"></i>
                                </div>
                                <div className="text-left whitespace-nowrap">
                                    <p className="font-bold text-sm">{plan.unitType || 'Unnamed'}</p>
                                    <p className={`text-[10px] font-semibold ${isActive ? theme.iconText : 'text-slate-400'}`}>{totalConfigUnits} Units</p>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingConfigIndex(idx);
                                    setNewConfigName(plan.unitType);
                                    setIsConfigModalOpen(true);
                                }}
                                className="absolute right-9 top-1/2 -translate-y-1/2 w-6 h-6 rounded-lg bg-slate-50 hover:bg-blue-50 text-slate-400 hover:text-blue-600 flex items-center justify-center transition-all opacity-40 group-hover:opacity-100 z-10 border border-slate-200 hover:border-blue-200"
                                title="Edit configuration name"
                            >
                                <i className="fa-solid fa-pen text-[10px]"></i>
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Are you sure you want to delete the configuration type "${plan.unitType}" and all of its variants?`)) {
                                        const newPlans = floorPlans.filter((_, i) => i !== idx);
                                        setFloorPlans(newPlans);
                                        setActiveConfigIndex(prev => {
                                            if (prev >= newPlans.length) {
                                                return Math.max(0, newPlans.length - 1);
                                            }
                                            return prev;
                                        });
                                        toast.success(`Deleted configuration type "${plan.unitType}"`);
                                    }
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-lg bg-slate-50 hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all opacity-40 group-hover:opacity-100 z-10 border border-slate-200 hover:border-rose-200"
                                title="Delete configuration"
                            >
                                <i className="fa-solid fa-trash-can text-[10px]"></i>
                            </button>
                        </div>
                    );
                })}
                <button 
                    type="button"
                    onClick={() => { setEditingConfigIndex(null); setNewConfigName(''); setIsConfigModalOpen(true); }}
                    className="flex items-center justify-center gap-2 px-5 py-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 text-blue-600 font-semibold text-sm hover:bg-blue-50 hover:border-blue-300 transition-colors whitespace-nowrap h-full"
                >
                    <i className="fa-solid fa-plus"></i> Add Configuration Type
                </button>
            </div>

            {/* Main Table Area */}
            {activePlan && (
                <div className="rounded-2xl overflow-hidden">
                    <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-white">
                        <h4 className="text-base font-bold text-slate-800">Floor Variants for {activePlan.unitType}</h4>
                        <div className="flex items-center gap-3">
                            <button type="button" onClick={() => handleOpenVariantModal(null)} className="px-4 py-2 border border-slate-200 text-blue-600 font-semibold text-sm rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-plus"></i> Add New Floor Variant
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                accept=".xlsx, .xls"
                                className="hidden"
                                onChange={handleImportExcel}
                            />
                            <button type="button" onClick={() => fileInputRef.current?.click()} className="px-4 py-2 border border-slate-200 text-blue-600 font-semibold text-sm rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-upload"></i> Bulk Upload
                            </button>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/80 border-b border-slate-200 text-[10px] font-extrabold text-slate-600 uppercase tracking-wider">
                                    <th className="py-4 px-4 text-center w-10">#</th>
                                    <th className="py-4 px-4">Floor Type<br/>Floor Range</th>
                                    <th className="py-4 px-4 text-center">Floor Plan (2D)</th>
                                    <th className="py-4 px-4 text-center">3D Plan</th>
                                    <th className="py-4 px-4 text-center">Info Images</th>
                                    <th className="py-4 px-4 text-center">Virtual Tour / Video</th>
                                    <th className="py-4 px-4 text-center">
                                        <div className="mb-2">Area Details (sq.ft.)</div>
                                        <div className="flex items-center justify-center gap-4 text-[9px]">
                                            <span className="w-10 text-center">Carpet</span>
                                            <span className="w-10 text-center">RERA</span>
                                            <span className="w-10 text-center">Built-up</span>
                                            <span className="w-16 text-center">Super Built-up</span>
                                        </div>
                                    </th>
                                    <th className="py-4 px-4 text-center">Base Price (₹)</th>
                                    <th className="py-4 px-4 text-center">Units on<br/>This Floor</th>
                                    <th className="py-4 px-4 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm">
                                {activePlan.subUnits.map((variant, vIdx) => {
                                    const rowThemes = [
                                        { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100' },
                                        { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-100' },
                                        { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-100' },
                                        { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100' },
                                    ];
                                    const rTheme = rowThemes[vIdx % rowThemes.length];
                                    return (
                                    <tr key={vIdx} className="border-b border-slate-100 hover:bg-slate-50/30 transition-colors align-middle">
                                        <td className="py-4 px-4 text-center font-bold text-slate-800">{vIdx + 1}</td>
                                        <td className="py-4 px-4 space-y-2">
                                            {variant.subUnitType && (
                                                <span className={`inline-block px-3 py-1 ${rTheme.bg} ${rTheme.text} text-[10px] font-bold rounded-lg uppercase tracking-wider border ${rTheme.border}`}>{variant.subUnitType}</span>
                                            )}
                                            <p className="font-bold text-slate-800 text-xs">{variant.subUnitVariant || '-'}</p>
                                            {variant.numberOfUnits && (
                                                <span className={`inline-block px-2 py-0.5 ${rTheme.bg} ${rTheme.text} text-[9px] font-bold rounded border ${rTheme.border}`}>{variant.numberOfUnits} Units</span>
                                            )}
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            {variant.image2DPreview || variant.image2D ? (
                                                <div className="flex flex-col items-center gap-1">
                                                    <div className="w-16 h-16 bg-white border border-slate-200 rounded overflow-hidden p-1 shadow-sm">
                                                        <img src={variant.image2DPreview || variant.image2D} className="w-full h-full object-contain" alt="2D Plan" />
                                                    </div>
                                                    <a href={variant.image2DPreview || variant.image2D} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 font-semibold hover:underline">View Full Size</a>
                                                </div>
                                            ) : <span className="text-[10px] text-slate-400">-</span>}
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            {variant.image3DPreview || variant.image3D ? (
                                                <div className="flex flex-col items-center gap-1">
                                                    <div className="w-16 h-16 bg-white border border-slate-200 rounded-lg overflow-hidden p-1 shadow-sm">
                                                        <img src={variant.image3DPreview || variant.image3D} className="w-full h-full object-cover rounded" alt="3D Plan" />
                                                    </div>
                                                    <a href={variant.image3DPreview || variant.image3D} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 font-bold hover:underline">View 3D</a>
                                                </div>
                                            ) : <span className="text-[10px] text-slate-400">-</span>}
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <div className="flex flex-col items-center gap-1">
                                                {/* Info Images Grid Mockup */}
                                                <div className="w-16 h-16 grid grid-cols-2 gap-0.5 bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                                                    <div className="bg-slate-300 w-full h-full relative"><img src="/placeholder-room.jpg" onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement?.classList.add('bg-slate-300') }} className="w-full h-full object-cover" /></div>
                                                    <div className="bg-slate-300 w-full h-full relative"><img src="/placeholder-room.jpg" onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement?.classList.add('bg-slate-300') }} className="w-full h-full object-cover" /></div>
                                                    <div className="bg-slate-300 w-full h-full relative"><img src="/placeholder-room.jpg" onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement?.classList.add('bg-slate-300') }} className="w-full h-full object-cover" /></div>
                                                    <div className="bg-slate-300 w-full h-full relative">
                                                        <img src="/placeholder-room.jpg" onError={(e) => { e.currentTarget.style.display='none'; e.currentTarget.parentElement?.classList.add('bg-slate-300') }} className="w-full h-full object-cover" />
                                                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                                            <span className="text-white text-[8px] font-bold">+6</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button type="button" className="text-[10px] text-blue-600 font-bold hover:underline mt-1">View All (10)</button>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <div className="flex flex-col items-center gap-1">
                                                {variant.virtual3DLink ? (
                                                    <div className="relative w-20 h-12 bg-slate-800 rounded-lg overflow-hidden shadow-sm flex items-center justify-center group cursor-pointer border border-slate-200">
                                                        <img src={variant.image3DPreview || variant.image3D || variant.image2DPreview || variant.image2D} onError={(e) => e.currentTarget.style.display='none'} className="absolute inset-0 w-full h-full object-cover opacity-60" />
                                                        <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-lg z-10 group-hover:scale-110 transition-transform">
                                                            <i className="fa-solid fa-play text-slate-900 text-[10px] ml-0.5"></i>
                                                        </div>
                                                        <div className="absolute top-1 left-1 bg-blue-600 text-white text-[6px] font-bold px-1 py-0.5 rounded shadow">360°</div>
                                                    </div>
                                                ) : (
                                                    <span className="text-[10px] text-slate-400">-</span>
                                                )}
                                                {variant.virtual3DLink && (
                                                    <a href={variant.virtual3DLink} target="_blank" rel="noreferrer" className="text-[9px] text-blue-600 font-bold hover:underline flex items-center gap-1 mt-1 truncate w-24" title={variant.virtual3DLink}>
                                                        {variant.virtual3DLink} <i className="fa-solid fa-arrow-up-right-from-square text-[8px]"></i>
                                                    </a>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-4 px-4">
                                            <div className="flex items-center justify-center gap-4 text-xs font-bold text-slate-800 text-center">
                                                <span className="w-10 text-center">{variant.carpetArea || '-'}</span>
                                                <span className="w-10 text-center">{variant.reraArea || '-'}</span>
                                                <span className="w-10 text-center">{variant.builtUpArea || '-'}</span>
                                                <span className="w-16 text-center">{variant.superBuiltUpArea || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <p className="font-extrabold text-slate-900">₹ {variant.price || '-'}</p>
                                            <p className="text-[9px] text-slate-500 font-bold leading-tight mt-0.5">{numberToWords(variant.price)}</p>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <span className={`inline-block px-3 py-1.5 ${rTheme.bg} ${rTheme.text} text-[11px] font-bold rounded-lg border ${rTheme.border}`}>{variant.numberOfUnits || '0'}<br/><span className="text-[9px]">Units</span></span>
                                        </td>
                                        <td className="py-4 px-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button type="button" onClick={() => handleOpenVariantModal(vIdx)} className="w-8 h-8 rounded-lg border border-slate-200 text-blue-600 hover:bg-blue-50 flex items-center justify-center transition-colors">
                                                    <i className="fa-solid fa-pen text-[10px]"></i>
                                                </button>
                                                <button type="button" onClick={() => handleDeleteVariant(vIdx)} className="w-8 h-8 rounded-lg border border-slate-200 text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors">
                                                    <i className="fa-solid fa-trash-can text-[10px]"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    
                    <div className="p-4 bg-white border-t border-slate-100">
                        <button type="button" onClick={() => handleOpenVariantModal(null)} className="w-full py-3 rounded-xl border border-slate-200 text-blue-600 font-bold text-sm flex items-center justify-center gap-2 hover:bg-blue-50 hover:border-blue-200 transition-colors">
                            <i className="fa-solid fa-plus"></i> Add Another Floor Variant
                        </button>
                    </div>
                </div>
            )}


            {/* Modals */}
            {isConfigModalOpen && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-2xl relative">
                        <button type="button" onClick={() => { setIsConfigModalOpen(false); setEditingConfigIndex(null); setNewConfigName(''); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><i className="fa-solid fa-xmark"></i></button>
                        <h3 className="text-lg font-bold text-slate-800 mb-4">{editingConfigIndex !== null ? 'Edit Configuration Type' : 'Add Configuration Type'}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-bold text-slate-600 block mb-2">Configuration Name</label>
                                <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold" placeholder="e.g. 1 BHK, 2 BHK Premium" value={newConfigName} onChange={e => setNewConfigName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSaveConfig()} autoFocus />
                            </div>
                            <button type="button" onClick={handleSaveConfig} className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-600/20 active:scale-95 transition-transform">{editingConfigIndex !== null ? 'Save Changes' : 'Add Configuration'}</button>
                        </div>
                    </div>
                </div>
            )}

            {isVariantModalOpen && (
                <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl w-full max-w-4xl p-0 shadow-2xl relative my-8 overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white z-10 sticky top-0">
                            <h3 className="text-xl font-bold text-slate-800">{editingVariantIndex !== null ? 'Edit Floor Variant' : 'Add New Floor Variant'}</h3>
                            <button type="button" onClick={() => setIsVariantModalOpen(false)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"><i className="fa-solid fa-xmark text-lg"></i></button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50 space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Floor Type (Badge)</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="e.g. Typical Floor" value={currentVariant.subUnitType} onChange={e => setCurrentVariant({...currentVariant, subUnitType: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Floor Range / Name *</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="e.g. 1st to 10th Floor" value={currentVariant.subUnitVariant} onChange={e => setCurrentVariant({...currentVariant, subUnitVariant: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Units on This Floor *</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="e.g. 10" value={currentVariant.numberOfUnits} onChange={e => setCurrentVariant({...currentVariant, numberOfUnits: e.target.value})} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Carpet Area</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="sq.ft." value={currentVariant.carpetArea} onChange={e => setCurrentVariant({...currentVariant, carpetArea: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">RERA Area</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="sq.ft." value={currentVariant.reraArea} onChange={e => setCurrentVariant({...currentVariant, reraArea: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Built-up Area</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="sq.ft." value={currentVariant.builtUpArea} onChange={e => setCurrentVariant({...currentVariant, builtUpArea: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Super Built-up</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="sq.ft." value={currentVariant.superBuiltUpArea} onChange={e => setCurrentVariant({...currentVariant, superBuiltUpArea: e.target.value})} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Base Price (₹) *</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="e.g. 45,50,000" value={currentVariant.price} onChange={e => setCurrentVariant({...currentVariant, price: e.target.value})} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-600 block">Virtual Tour Link</label>
                                    <input type="text" className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none focus:border-blue-500 text-sm font-semibold shadow-sm" placeholder="https://..." value={currentVariant.virtual3DLink} onChange={e => setCurrentVariant({...currentVariant, virtual3DLink: e.target.value})} />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-slate-600 block">2D Floor Plan Image</label>
                                    <div className="relative aspect-video rounded-xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center overflow-hidden hover:border-blue-400 transition-colors group">
                                        {(currentVariant.image2DPreview || currentVariant.image2D) ? (
                                            <>
                                                <img src={currentVariant.image2DPreview || currentVariant.image2D} className="w-full h-full object-contain p-4" alt="2D" />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                    <span className="text-white text-xs font-bold">Change Image</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setCurrentVariant({
                                                            ...currentVariant,
                                                            image2D: '',
                                                            image2DFile: undefined,
                                                            image2DPreview: ''
                                                        });
                                                    }}
                                                    className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95"
                                                    title="Delete 2D Plan"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <div className="text-center">
                                                <i className="fa-solid fa-cloud-arrow-up text-3xl text-slate-300 mb-2"></i>
                                                <p className="text-xs font-semibold text-slate-500">Upload 2D Plan</p>
                                            </div>
                                        )}
                                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file) setCurrentVariant({...currentVariant, image2DFile: file, image2DPreview: URL.createObjectURL(file)});
                                        }} />
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-slate-600 block">3D Floor Plan Image</label>
                                    <div className="relative aspect-video rounded-xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center overflow-hidden hover:border-blue-400 transition-colors group">
                                        {(currentVariant.image3DPreview || currentVariant.image3D) ? (
                                            <>
                                                <img src={currentVariant.image3DPreview || currentVariant.image3D} className="w-full h-full object-contain p-4" alt="3D" />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                    <span className="text-white text-xs font-bold">Change Image</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setCurrentVariant({
                                                            ...currentVariant,
                                                            image3D: '',
                                                            image3DFile: undefined,
                                                            image3DPreview: ''
                                                        });
                                                    }}
                                                    className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95"
                                                    title="Delete 3D Plan"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <div className="text-center">
                                                <i className="fa-solid fa-cube text-3xl text-slate-300 mb-2"></i>
                                                <p className="text-xs font-semibold text-slate-500">Upload 3D Plan</p>
                                            </div>
                                        )}
                                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file) setCurrentVariant({...currentVariant, image3DFile: file, image3DPreview: URL.createObjectURL(file)});
                                        }} />
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <label className="text-xs font-bold text-slate-600 block">Info Image</label>
                                    <div className="relative aspect-video rounded-xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center overflow-hidden hover:border-blue-400 transition-colors group">
                                        {(currentVariant.infoImagePreview || currentVariant.infoImage) ? (
                                            <>
                                                <img src={currentVariant.infoImagePreview || currentVariant.infoImage} className="w-full h-full object-cover p-1" alt="Info" />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                                    <span className="text-white text-xs font-bold">Change Image</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        setCurrentVariant({
                                                            ...currentVariant,
                                                            infoImage: '',
                                                            infoImageFile: undefined,
                                                            infoImagePreview: ''
                                                        });
                                                    }}
                                                    className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95"
                                                    title="Delete Info Image"
                                                >
                                                    <i className="fa-solid fa-trash-can text-xs"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <div className="text-center">
                                                <i className="fa-solid fa-images text-3xl text-slate-300 mb-2"></i>
                                                <p className="text-xs font-semibold text-slate-500">Upload Info Image</p>
                                            </div>
                                        )}
                                        <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file) setCurrentVariant({...currentVariant, infoImageFile: file, infoImagePreview: URL.createObjectURL(file)});
                                        }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 border-t border-slate-100 bg-white flex items-center justify-end gap-4 shrink-0">
                            <button type="button" onClick={() => setIsVariantModalOpen(false)} className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-colors">Cancel</button>
                            <button type="button" onClick={handleSaveVariant} className="px-8 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-600/20 active:scale-95 transition-transform">{editingVariantIndex !== null ? 'Save Changes' : 'Add Variant'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Step4FloorPlan;
